import { INGLIS_BLUE } from '../../constants/colors'
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import sitesData from '../../data/sites.json'
import { spokenLabel } from '../../constants/mapConfig'
import type { Filters, FilterGroupKey, FilterOption, SitesData } from '../../types'
import SpokenAT from '../SpokenAT'

const ALL_SITES = (sitesData as SitesData)?.sites ?? []

function countMatching(allSites: SitesData['sites'], groupValue: string) {
  if (groupValue === 'TECHOWL') return 1 // one aggregate marker, not a count of sites.json entries
  return allSites.filter(s => s.category === groupValue).length
}

interface FilterDropdownProps {
  label: string
  filterKey: FilterGroupKey
  options: FilterOption[]
  filters: Filters
  toggleFilter: (group: FilterGroupKey, value: string) => void
  clearGroup: (group: FilterGroupKey) => void
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export default function FilterDropdown({
  label, filterKey, options, filters,
  toggleFilter, clearGroup,
  isOpen, onOpen, onClose,
}: FilterDropdownProps) {
  // panelRef is the whole popup (used for outside-click detection); listboxRef is the options
  // container inside it, which is the part that actually holds focus and keyboard handling.
  const panelRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const selected = filters[filterKey] as string[]
  // Nothing checked is the default, unfiltered state — everything is on the map (see
  // isCategoryVisible in hooks/useFilteredSites.ts). So "default" here is simply an empty
  // selection, and any checked option means the view is narrowed.
  const isDefault = selected.length === 0
  const triggerActive = isOpen || !isDefault

  const hintId = `fd-hint-${filterKey}`

  const [activeIndex, setActiveIndex] = useState(-1)

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    setActiveIndex(isOpen ? 0 : -1)
  }

  // The panel is portaled to <body> (see below) so it renders in fixed/viewport coordinates
  // instead of `position: absolute` relative to the trigger — CombinedBar scrolls horizontally
  // on narrow screens (`overflowX: 'auto'`), and per the CSS spec that forces its vertical
  // overflow to clip too, cutting the dropdown panel off after one row with no way to scroll to
  // the rest. Rendering outside that container sidesteps the clipping entirely.
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(null)

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPanelPos(null)
      return
    }
    function updatePosition() {
      const rect = triggerRef.current!.getBoundingClientRect()
      const width = Math.min(290, window.innerWidth * 0.9)
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8)
      setPanelPos({ top: rect.bottom + 4, left, width })
    }
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    // Depends on panelPos (not just isOpen): the panel is portaled and only actually mounts once
    // panelPos is computed, one render after isOpen flips true — focusing here (rather than in
    // the isOpen-keyed effect above) guarantees panelRef.current is attached by the time we call it.
    if (isOpen && panelPos) listboxRef.current?.focus()
  }, [isOpen, panelPos])

  useEffect(() => {
    if (!isOpen) return
    function handleMouseDown(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [isOpen, onClose])

  function handlePanelKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => (i + 1) % options.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => (i - 1 + options.length) % options.length)
    } else if (e.key === 'Enter' || e.key === ' ') {
      // Enter is the primary activation key here, with Space kept as a secondary: people arriving
      // from a form or a list expect Enter to commit the highlighted row, and Space-only meant a
      // keyboard user could arrow onto an option, press Enter, and get no feedback at all. The
      // visible "Press Enter to select" hint below names the same key.
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < options.length) {
        const { value } = options[activeIndex]
        const count = countMatching(ALL_SITES, value)
        // Only block *checking* a zero-match option — an already-checked option must stay
        // togglable off even at 0 matches, or it gets stuck selected forever once some other
        // filter drops its count to zero (see onClick below for the full explanation).
        if (count > 0 || selected.includes(value)) toggleFilter(filterKey, value)
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      triggerRef.current?.focus()
      onClose()
    } else if (e.key === 'Tab') {
      // The panel is portaled to <body> (see below), so its DOM position has nothing to do with
      // the trigger button's — leaving Tab to its native default here would move focus based on
      // wherever the portal actually landed (typically the very end of <body>), not "the next
      // control after the trigger." Re-focusing the trigger synchronously, without
      // preventDefault, means the browser computes Tab's default action from that new anchor
      // instead — so focus continues naturally to whatever comes after the trigger.
      triggerRef.current?.focus()
      onClose()
    } else if (e.key === 'Home') {
      e.preventDefault()
      setActiveIndex(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setActiveIndex(options.length - 1)
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (isOpen ? onClose() : onOpen())}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        // Explicit name instead of letting it fall out of the visible content: the count badge
        // and caret glyph below are both aria-hidden (decorative/redundant with aria-expanded),
        // so without this the button's name would just be {label} with the selection count lost
        // entirely rather than surfaced in a readable form.
        aria-label={spokenLabel(isDefault ? label : `${label}, ${selected.length} selected`)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
          // 44px per WCAG 2.5.5 (AAA), matching the markers and list-header buttons rather than
          // sitting on the 24px 2.5.8 floor — this is the primary control on a phone.
          padding: '0 13px', minHeight: 44, borderRadius: 5,
          fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
          whiteSpace: 'nowrap', border: '1px solid',
          background: triggerActive ? INGLIS_BLUE : '#f7f9fc',
          borderColor: triggerActive ? INGLIS_BLUE : '#c8d4e0',
          color: triggerActive ? '#fff' : 'var(--text-strong)',
        }}
      >
        <span aria-hidden="true">{label}</span>
        {!isDefault && (
          // Decorative — the button's aria-label above already covers "N selected" in readable
          // form; without aria-hidden here, AT would additionally read the bare number as if it
          // were part of the name (e.g. "Region 3 ▾").
          <span aria-hidden="true" style={{
            background: '#fff', color: INGLIS_BLUE, borderRadius: 999,
            minWidth: 18, height: 18, padding: '0 4px',
            fontSize: '0.75rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {selected.length}
          </span>
        )}
        <span aria-hidden="true" style={{ fontSize: '0.75rem', opacity: 0.8 }}>▾</span>
      </button>

      {isOpen && panelPos && createPortal(
        // Plain container, not the listbox itself: role="listbox" may only contain options
        // (aria-required-children), and this panel also holds the group header, the "Show all"
        // button and the keyboard hint. The listbox role lives on the options container further
        // down, which is also what takes focus and handles keys.
        <div
          ref={panelRef}
          style={{
            position: 'fixed', top: panelPos.top, left: panelPos.left, zIndex: 50,
            width: panelPos.width, maxHeight: 'min(400px, 70vh)',
            display: 'flex', flexDirection: 'column',
            background: '#fff', border: '1px solid #c8d4e0',
            borderRadius: 7, boxShadow: '0 6px 20px rgba(16,28,48,.14)',
            overflow: 'hidden',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 12px 7px', borderBottom: '1px solid #eef2f7', flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)' }}>
              <SpokenAT text={label} />
            </span>
            {!isDefault && (
              <button
                type="button"
                // Clearing the group returns it to the empty/unfiltered selection, which puts every
                // site back on the map — hence "Show all" rather than the old "Select all", which
                // now describes neither the mechanism (nothing gets checked) nor the result.
                onClick={() => clearGroup(filterKey)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: INGLIS_BLUE, fontWeight: 700, fontSize: '0.82rem', padding: 0 }}
              >
                Show all
              </button>
            )}
          </div>
          {/* Keyboard hint. The options aren't focusable themselves (aria-activedescendant pattern),
              so nothing about this list tells a keyboard user which key commits the highlighted row
              — spelling it out is cheaper than expecting people to guess. Referenced by
              aria-describedby on the listbox so it's announced once on open rather than re-read
              with every arrow key. */}
          <p
            id={hintId}
            style={{
              margin: 0, padding: '6px 12px', flexShrink: 0,
              borderBottom: '1px solid #eef2f7', background: '#f8fafc',
              fontSize: '0.72rem', color: 'var(--text-muted)',
            }}
          >
            Use arrow keys to move, then press Enter to select.
          </p>
          <div
            ref={listboxRef}
            role="listbox"
            aria-label={spokenLabel(label)}
            aria-describedby={hintId}
            aria-multiselectable="true"
            tabIndex={0}
            aria-activedescendant={
              activeIndex >= 0 && activeIndex < options.length
                ? `fd-opt-${filterKey}-${options[activeIndex].value}`
                : undefined
            }
            onKeyDown={handlePanelKeyDown}
            // The focus indicator lives on the active option (.fd-option.is-active in index.css),
            // not on this container: this element holds DOM focus but the thing a user is actually
            // moving is the aria-activedescendant highlight, so a ring around the whole scroll box
            // would point at the wrong thing. The container still gets its own ring while no
            // option is active (see .fd-listbox:focus-visible), so focus is never invisible.
            className="fd-listbox"
            style={{ overflowY: 'auto' }}
          >
            {options.map(({ value, label: optLabel, swatch }, idx) => {
              const count = countMatching(ALL_SITES, value)
              const isChecked = selected.includes(value)
              // Disabling on count===0 alone traps an already-checked option: once some other
              // filter (e.g. Site Type) drops its match count to 0, isDisabled would block every
              // future click, so the user could never uncheck it again and the badge count would
              // stay inflated by a selection that no longer does anything. Only block *checking*
              // a zero-match option — unchecking one that's already selected must always work.
              const isDisabled = count === 0 && !isChecked
              const isActive = activeIndex === idx
              return (
                // A real nested <input>/<label> here would be a WCAG "nested interactive controls"
                // violation (role="option" is itself interactive, and axe/AT flag a focusable
                // control inside it even with aria-hidden+tabIndex=-1). This row IS the option —
                // there's no separate control to focus, so a plain div with onClick + the listbox
                // keyboard handling in handlePanelKeyDown covers all interaction; the checkmark
                // below is purely decorative, driven by isChecked.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- roving aria-activedescendant listbox pattern: the panel container (tabIndex=0) owns focus and keyboard handling (handlePanelKeyDown above), individual options are intentionally not focusable/tabbable themselves; onClick here is the mouse-only path
                <div
                  key={value}
                  id={`fd-opt-${filterKey}-${value}`}
                  role="option"
                  aria-selected={isChecked}
                  aria-disabled={isDisabled}
                  // spokenLabel: says "Assistive Technology" where the visible row says "AT",
                  // so the option is announced in words regardless of how the screen reader
                  // handles the dotted abbreviation.
                  aria-label={`${spokenLabel(optLabel)}, ${count} site${count === 1 ? '' : 's'}`}
                  onClick={() => !isDisabled && toggleFilter(filterKey, value)}
                  // fd-option/is-active: Windows forced-colors mode overrides this row's inline
                  // background, so the roving-focus highlight (this list's only focus indicator —
                  // options aren't themselves focusable, see the eslint-disable note above) would
                  // otherwise disappear for keyboard users. The class gives forced-colors mode
                  // something to hang an outline on; see the @media (forced-colors: active) block
                  // in index.css.
                  className={isActive ? 'fd-option is-active' : 'fd-option'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    // 44px touch target, same reasoning as the trigger above.
                    padding: '0 12px', minHeight: 44,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    background: isActive ? '#eef4ff' : 'transparent',
                  }}
                >
                  <span
                    aria-hidden="true"
                    // fd-checkbox/is-checked: forced-colors mode strips the inline
                    // background/border colors below, so checked vs. unchecked become
                    // indistinguishable — the class restores that distinction with system colors.
                    className={isChecked ? 'fd-checkbox is-checked' : 'fd-checkbox'}
                    style={{
                      width: 14, height: 14, flexShrink: 0, borderRadius: 3,
                      // #94a3b8 was 2.56:1 against the white row — under the 3:1 WCAG 1.4.11
                      // floor for a control's state indicator, and this border IS the entire
                      // unchecked state. #64707f is 4.36:1.
                      border: `1.5px solid ${isChecked ? INGLIS_BLUE : '#64707f'}`,
                      background: isChecked ? INGLIS_BLUE : 'transparent',
                      opacity: isDisabled ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {isChecked && (
                      <svg width="9" height="7" viewBox="0 0 9 7" fill="none" className="fd-checkmark">
                        <path d="M1 3.5L3.2 5.7L8 1" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  {swatch && (
                    <span style={{
                      width: 9, height: 9, borderRadius: '50%',
                      background: swatch, flexShrink: 0,
                      opacity: isDisabled ? 0.4 : 1,
                    }} />
                  )}
                  <span style={{
                    fontSize: '0.85rem', fontWeight: 500,
                    color: isDisabled ? '#5f6368' : 'var(--text-strong)',
                    flex: 1, whiteSpace: 'normal',
                  }}>
                    <SpokenAT text={optLabel} />
                  </span>
                  <span style={{
                    fontSize: '0.78rem',
                    color: isDisabled ? '#5f6368' : 'var(--text-muted)',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {count}
                  </span>
                </div>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
